import React, { createContext, useContext, useState, useEffect } from 'react';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [vapidPublicKey, setVapidPublicKey] = useState('');

  useEffect(() => {
    // TODO: Replace with your actual VAPID public key after generating it
    // Run: npx web-push generate-vapid-keys
    // Webpack DefinePlugin will replace process.env.REACT_APP_VAPID_PUBLIC_KEY at build time
    let publicKey = '';
    try {
      // DefinePlugin replaces this at build time, so it's safe to access
      if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_VAPID_PUBLIC_KEY) {
        publicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      }
    } catch (e) {
      // process.env not available, use empty string
      console.warn('VAPID public key not available:', e);
    }
    setVapidPublicKey(publicKey);
    
    // Load saved subscription
    const savedSubscription = localStorage.getItem('@calpro_push_subscription');
    if (savedSubscription) {
      try {
        const sub = JSON.parse(savedSubscription);
        setSubscription(sub);
        setIsSubscribed(true);
      } catch (e) {
        console.error('Error loading subscription:', e);
      }
    }
  }, []);

  const subscribe = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      throw new Error('Push notifications are not supported in this browser');
    }

    if (!vapidPublicKey) {
      throw new Error('VAPID public key not configured. Please set REACT_APP_VAPID_PUBLIC_KEY environment variable.');
    }

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }

      // Register service worker
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      // Save subscription
      localStorage.setItem('@calpro_push_subscription', JSON.stringify(sub));
      setSubscription(sub);
      setIsSubscribed(true);

      // Send subscription to server
      try {
        await fetch('/api/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscription: sub }),
        });
      } catch (e) {
        console.warn('Could not save subscription to server:', e);
        // Continue anyway - subscription is saved locally
      }

      return true;
    } catch (error) {
      console.error('Error subscribing:', error);
      throw error;
    }
  };

  const unsubscribe = async () => {
    if (!subscription) return;

    try {
      await subscription.unsubscribe();
      localStorage.removeItem('@calpro_push_subscription');
      setSubscription(null);
      setIsSubscribed(false);
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      throw error;
    }
  };

  const sendTestNotification = async (title, body) => {
    if (!subscription) {
      throw new Error('Not subscribed to push notifications');
    }

    const response = await fetch('/api/send-push', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscription,
        title,
        body,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send notification');
    }

    return true;
  };

  return (
    <NotificationContext.Provider
      value={{
        isSubscribed,
        subscribe,
        unsubscribe,
        sendTestNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

